import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Award, MapPin, ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { GoogleBadge } from "@/components/landing/GoogleBadge";

const CITY_LINKS = [
  { name: "Moncton", slug: "moncton" },
  { name: "Dieppe", slug: "dieppe" },
  { name: "Fredericton", slug: "fredericton" },
  { name: "Saint John", slug: "saint-john" },
  { name: "Woodstock", slug: "woodstock" },
  { name: "Bathurst", slug: "bathurst" },
  { name: "Miramichi", slug: "miramichi" },
];

const HERO_VIDEO = "/assets/hero-install.mp4";
const HERO_VIDEO_MOBILE = "/assets/hero-install-mobile.mp4";
const HERO_POSTER = "/assets/hero-poster.webp";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export const Hero = () => {
  // JS-based source selection: `media` on <source> is unreliable in some mobile browsers
  const [isMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const videoSrc = isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO;
  return (
    <section id="top" data-testid="hero-section" className="relative grain min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <video
          key={videoSrc}
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_POSTER}
          onCanPlay={(e) => {
            // iOS Low Power Mode can block autoplay — retry play() and silently keep poster if refused
            const p = e.target.play();
            if (p && p.catch) p.catch(() => {});
          }}
          // 2x speed is baked into the encoded file. Mobile crop: keep the lower-right
          // camera + installer in view; desktop framing stays centered/unchanged.
          style={{ objectPosition: isMobile ? "72% 88%" : "center" }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative w-full container-x pt-14 pb-24 lg:pt-20 lg:pb-40"
      >
        <motion.p
          variants={item}
          data-testid="hero-category-line"
          className="text-center text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-8 -mt-8 lg:-mt-14"
        >
          Security&nbsp;&nbsp;|&nbsp;&nbsp;Technology&nbsp;&nbsp;|&nbsp;&nbsp;Lighting
        </motion.p>
        <motion.p
          variants={item}
          className="text-left text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-white/70 mb-3"
        >
          Serving all of New Brunswick
        </motion.p>
        <motion.nav
          variants={item}
          data-testid="hero-cities-line"
          aria-label="Service areas"
          className="flex flex-wrap justify-start gap-x-3 gap-y-1.5 text-[10.5px] sm:text-[12px] uppercase tracking-[0.14em] font-medium text-[#D4AF37] mb-8 text-left"
        >
          {CITY_LINKS.map((c, i) => (
            <span key={c.slug} className="flex items-center gap-3">
              <Link
                to={`/service-areas/${c.slug}/`}
                data-testid={`city-link-${c.slug}`}
                className="underline underline-offset-4 decoration-[#D4AF37]/50 hover:text-white hover:decoration-white transition-colors"
              >
                {c.name}
              </Link>
              {i < CITY_LINKS.length - 1 && <span aria-hidden="true">•</span>}
            </span>
          ))}
        </motion.nav>
        <motion.h1
          variants={item}
          className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none text-white max-w-3xl"
        >
          Professional Security & Technology Installation
        </motion.h1>
        <motion.p variants={item} className="mt-4 text-lg sm:text-xl font-medium text-white/90">
          Peace of mind, every day.
        </motion.p>
        <motion.p variants={item} className="mt-6 text-[13px] sm:text-lg leading-relaxed text-white/85 max-w-xl">
          From surveillance cameras to smart locks and whole-home WiFi — SafeHome Security Solutions designs
          and installs complete security and AV systems for homes and businesses across the province.
        </motion.p>

        <motion.div variants={item} className="mt-10 flex flex-wrap gap-4">
          <a
            href="#quote"
            data-testid="hero-quote-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] sm:text-base sm:px-7 sm:py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
          >
            Get a Free Quote <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] sm:text-base sm:px-7 sm:py-3.5 rounded-md bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
          >
            View Services
          </a>
        </motion.div>

        <motion.p variants={item} className="mt-4 text-sm font-semibold text-[#D4AF37]" data-testid="hero-offer-line">
          Get 20% OFF your first installation — claim your free quote today
        </motion.p>

        <motion.p variants={item} className="mt-3 text-xs sm:text-sm text-white/80 tracking-wide font-medium">
          • Quality Work&nbsp;&nbsp;&nbsp;•&nbsp;Competitive Pricing
        </motion.p>

        <motion.div variants={item} className="mt-8">
          <div className="scale-[0.8] origin-left">
            <GoogleBadge />
          </div>
        </motion.div>

        <motion.div variants={item} className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
          {[
            { icon: ShieldCheck, text: "Licensed & Insured" },
            { icon: BadgeCheck, text: "Authorized Verkada Dealer" },
            { icon: Award, text: "3-Year Warranty" },
            { icon: MapPin, text: "Local New Brunswick Team" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-2.5 text-white/90">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-medium">{text}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.a
        href="#services"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-1 text-white/70 hover:text-white transition-colors"
      >
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}>
          <ChevronDown className="w-5 h-5 text-[#D4AF37]" />
        </motion.span>
      </motion.a>
    </section>
  );
};
